import React, { useState, useEffect, useCallback } from 'react';
import { API, ax, btn, inp, card } from './adminHelpers';

export default function OrganizerHistoryTab() {
  const [data, setData] = useState({ summary: {}, organizers: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Selected organizer for detailed event log modal
  const [selectedOrganizerLog, setSelectedOrganizerLog] = useState(null);

  // New Organizer Modal state
  const [newOrgModal, setNewOrgModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgEmail, setNewOrgEmail] = useState('');
  const [newOrgPassword, setNewOrgPassword] = useState('');
  const [newOrgPhone, setNewOrgPhone] = useState('');
  const [orgCreating, setOrgCreating] = useState(false);
  const [modalError, setModalError] = useState('');
  const [successToast, setSuccessToast] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const res = await ax().get(`${API}/api/admin/organizers/history`, { params });
      setData(res.data || { summary: {}, organizers: [] });
    } catch (err) {
      console.error('[OrganizerHistoryTab] loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateOrganizer = async (e) => {
    e.preventDefault();
    setOrgCreating(true);
    setModalError('');

    try {
      await ax().post(`${API}/api/admin/organizers`, {
        name: newOrgName,
        email: newOrgEmail,
        password: newOrgPassword,
        phone_number: newOrgPhone,
      });

      setSuccessToast(`Organizer "${newOrgName}" created successfully!`);
      setNewOrgModal(false);
      setNewOrgName('');
      setNewOrgEmail('');
      setNewOrgPassword('');
      setNewOrgPhone('');
      loadData();
      setTimeout(() => setSuccessToast(''), 4000);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create organizer');
    } finally {
      setOrgCreating(false);
    }
  };

  const filteredOrganizers = (data.organizers || []).filter((org) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return org.name?.toLowerCase().includes(term) || org.email?.toLowerCase().includes(term);
  });

  const summary = data.summary || {};

  return (
    <div>
      {/* Top Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff' }}>
            Event Organizers & Performance History
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#a0a0b8' }}>
            Track assigned event fulfillment, scheduled vs logged hours, and on-site logs
          </p>
        </div>

        <button
          onClick={() => setNewOrgModal(true)}
          style={{
            ...btn('#27C85A'),
            padding: '10px 18px',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          ➕ Add New Organizer
        </button>
      </div>

      {successToast && (
        <div
          style={{
            background: 'rgba(39, 200, 90, 0.15)',
            border: '1px solid rgba(39, 200, 90, 0.4)',
            color: '#27C85A',
            padding: '12px 18px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          ✓ {successToast}
        </div>
      )}

      {/* Summary KPI Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div style={{ ...card, marginBottom: 0, border: '1px solid rgba(159, 92, 255, 0.3)' }}>
          <div style={{ fontSize: 11, color: '#a0a0b8', textTransform: 'uppercase', fontWeight: 700 }}>
            Active Organizers
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#9F5CFF', marginTop: 4 }}>
            {summary.totalOrganizers || 0}
          </div>
          <div style={{ fontSize: 11, color: '#7c7c9c', marginTop: 2 }}>Field team members</div>
        </div>

        <div style={{ ...card, marginBottom: 0, border: '1px solid rgba(0, 210, 255, 0.3)' }}>
          <div style={{ fontSize: 11, color: '#a0a0b8', textTransform: 'uppercase', fontWeight: 700 }}>
            Assigned Events
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#00D2FF', marginTop: 4 }}>
            {summary.totalAssignedEvents || 0}
          </div>
          <div style={{ fontSize: 11, color: '#7c7c9c', marginTop: 2 }}>Across all dates</div>
        </div>

        <div style={{ ...card, marginBottom: 0, border: '1px solid rgba(39, 200, 90, 0.3)' }}>
          <div style={{ fontSize: 11, color: '#a0a0b8', textTransform: 'uppercase', fontWeight: 700 }}>
            Events Completed
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#27C85A', marginTop: 4 }}>
            {summary.totalCompletedEvents || 0}
          </div>
          <div style={{ fontSize: 11, color: '#7c7c9c', marginTop: 2 }}>Verified on-site wrap ups</div>
        </div>

        <div style={{ ...card, marginBottom: 0, border: '1px solid rgba(245, 166, 35, 0.3)' }}>
          <div style={{ fontSize: 11, color: '#a0a0b8', textTransform: 'uppercase', fontWeight: 700 }}>
            Hours Delivered
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#f5a623', marginTop: 4 }}>
            {summary.totalHoursDelivered || 0} hrs
          </div>
          <div style={{ fontSize: 11, color: '#7c7c9c', marginTop: 2 }}>
            Sched: {summary.totalHoursScheduled || 0} hrs
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div
        style={{
          ...card,
          padding: '16px 20px',
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <div style={{ flex: 1, minWidth: 200 }}>
          <input
            type="text"
            placeholder="🔍 Search organizer by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={inp(false)}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#a0a0b8' }}>From:</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ ...inp(false), width: 140 }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#a0a0b8' }}>To:</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ ...inp(false), width: 140 }}
          />
        </div>

        {(startDate || endDate) && (
          <button
            onClick={() => {
              setStartDate('');
              setEndDate('');
            }}
            style={{ ...btn('rgba(255,255,255,0.1)', true), fontSize: 11 }}
          >
            Clear Dates
          </button>
        )}
      </div>

      {/* Organizers Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#a0a0b8' }}>
          Loading organizer metrics…
        </div>
      ) : filteredOrganizers.length === 0 ? (
        <div
          style={{
            ...card,
            textAlign: 'center',
            padding: '48px 20px',
            color: '#7c7c9c',
          }}
        >
          No organizers found matching your criteria.
        </div>
      ) : (
        <div
          style={{
            ...card,
            padding: 0,
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
                <th style={{ padding: '14px 18px', fontSize: 11, color: '#a0a0b8', textTransform: 'uppercase' }}>
                  Organizer
                </th>
                <th style={{ padding: '14px 18px', fontSize: 11, color: '#a0a0b8', textTransform: 'uppercase' }}>
                  Assigned
                </th>
                <th style={{ padding: '14px 18px', fontSize: 11, color: '#a0a0b8', textTransform: 'uppercase' }}>
                  Completed
                </th>
                <th style={{ padding: '14px 18px', fontSize: 11, color: '#a0a0b8', textTransform: 'uppercase' }}>
                  In Progress / Active
                </th>
                <th style={{ padding: '14px 18px', fontSize: 11, color: '#a0a0b8', textTransform: 'uppercase' }}>
                  Hours (Logged / Sched)
                </th>
                <th style={{ padding: '14px 18px', fontSize: 11, color: '#a0a0b8', textTransform: 'uppercase' }}>
                  Fulfillment Rate
                </th>
                <th style={{ padding: '14px 18px', fontSize: 11, color: '#a0a0b8', textTransform: 'uppercase' }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOrganizers.map((org) => {
                const isPerfect = org.completionRate === 100 && org.completedCount > 0;

                return (
                  <tr
                    key={org.id}
                    style={{
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {/* Name & Contact */}
                    <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{org.name}</div>
                      <div style={{ fontSize: 12, color: '#a0a0b8', marginTop: 2 }}>{org.email}</div>
                    </td>

                    {/* Assigned */}
                    <td style={{ padding: '16px 18px', verticalAlign: 'middle', fontSize: 14, fontWeight: 700, color: '#fff' }}>
                      {org.assignedCount}
                    </td>

                    {/* Completed */}
                    <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
                      <span
                        style={{
                          background: 'rgba(39, 200, 90, 0.15)',
                          color: '#27C85A',
                          fontSize: 12,
                          fontWeight: 800,
                          padding: '3px 10px',
                          borderRadius: 20,
                        }}
                      >
                        ✓ {org.completedCount}
                      </span>
                    </td>

                    {/* Active */}
                    <td style={{ padding: '16px 18px', verticalAlign: 'middle', fontSize: 13, color: '#00D2FF' }}>
                      {org.inProgressCount > 0 ? (
                        <span style={{ fontWeight: 700, color: '#9F5CFF' }}>
                          🔴 {org.inProgressCount} Live Now
                        </span>
                      ) : org.acceptedCount > 0 ? (
                        <span>🕒 {org.acceptedCount} Scheduled</span>
                      ) : (
                        <span style={{ color: '#666' }}>—</span>
                      )}
                    </td>

                    {/* Hours */}
                    <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#f5a623' }}>
                        {org.actualHours} hrs logged
                      </div>
                      <div style={{ fontSize: 11, color: '#7c7c9c', marginTop: 2 }}>
                        {org.scheduledHours} hrs scheduled
                      </div>
                    </td>

                    {/* Completion Rate Bar */}
                    <td style={{ padding: '16px 18px', verticalAlign: 'middle', minWidth: 140 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 10, overflow: 'hidden' }}>
                          <div
                            style={{
                              height: '100%',
                              width: `${org.completionRate}%`,
                              background: isPerfect
                                ? '#27C85A'
                                : org.completionRate > 50
                                ? '#9F5CFF'
                                : '#f5a623',
                            }}
                          />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: isPerfect ? '#27C85A' : '#fff' }}>
                          {org.completionRate}%
                        </span>
                      </div>
                    </td>

                    {/* Action */}
                    <td style={{ padding: '16px 18px', verticalAlign: 'middle' }}>
                      <button
                        onClick={() => setSelectedOrganizerLog(org)}
                        style={{
                          ...btn('#9F5CFF', true),
                          fontSize: 11,
                          padding: '6px 14px',
                        }}
                      >
                        📜 View Event Log ({org.events?.length || 0})
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detailed Event Breakdown Modal */}
      {selectedOrganizerLog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 10000,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 720,
              background: '#121832',
              borderRadius: 18,
              border: '1px solid rgba(159,92,255,0.3)',
              padding: 24,
              maxHeight: '85vh',
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, color: '#fff', fontWeight: 700 }}>
                  Event Log: {selectedOrganizerLog.name}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#a0a0b8' }}>
                  {selectedOrganizerLog.email} • {selectedOrganizerLog.events?.length || 0} total assignments
                </p>
              </div>
              <button
                onClick={() => setSelectedOrganizerLog(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#a0a0b8',
                  fontSize: 20,
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            {selectedOrganizerLog.events?.length === 0 ? (
              <p style={{ color: '#7c7c9c', textAlign: 'center', padding: '30px 0' }}>
                No events assigned to this organizer yet.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {selectedOrganizerLog.events?.map((e) => (
                  <div
                    key={e.id}
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 12,
                      padding: '14px 16px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                      <div>
                        <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#9F5CFF', marginRight: 8 }}>
                          #{e.id}
                        </span>
                        <strong style={{ fontSize: 14, color: '#fff' }}>📅 {e.event_date}</strong>
                        <span style={{ fontSize: 12, color: '#a0a0b8', marginLeft: 8 }}>
                          🕒 {e.start_time} - {e.end_time}
                        </span>
                      </div>

                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          padding: '2px 8px',
                          borderRadius: 12,
                          background:
                            e.assignment_status === 'completed'
                              ? 'rgba(39,200,90,0.15)'
                              : e.assignment_status === 'in_progress'
                              ? 'rgba(159,92,255,0.2)'
                              : 'rgba(245,166,35,0.15)',
                          color:
                            e.assignment_status === 'completed'
                              ? '#27C85A'
                              : e.assignment_status === 'in_progress'
                              ? '#9F5CFF'
                              : '#f5a623',
                        }}
                      >
                        {e.assignment_status}
                      </span>
                    </div>

                    <div style={{ fontSize: 13, color: '#fff' }}>🏛️ {e.venue_name || 'Venue TBD'}</div>
                    {e.venue_address && (
                      <div style={{ fontSize: 11, color: '#7c7c9c' }}>📍 {e.venue_address}</div>
                    )}

                    <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: '#a0a0b8' }}>
                      <div>Client: <strong style={{ color: '#fff' }}>{e.customer_name}</strong></div>
                      <div>Booth: <strong style={{ color: '#fff' }}>{e.booth_type}</strong></div>
                      <div>
                        Logged: <strong style={{ color: '#27C85A' }}>{e.actual_hours_worked || 0} hrs</strong>
                        {' '}(Sched: {e.scheduled_hours} hrs)
                      </div>
                    </div>

                    {e.organizer_notes && (
                      <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 6, fontSize: 12, color: '#f5a623' }}>
                        📝 Notes: "{e.organizer_notes}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Organizer Modal */}
      {newOrgModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 10000,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 460,
              background: '#121832',
              borderRadius: 18,
              border: '1px solid rgba(39,200,90,0.3)',
              padding: 24,
            }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 18, color: '#fff', fontWeight: 700 }}>
              ➕ Add New Event Organizer
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#a0a0b8' }}>
              Create an organizer staff login with direct access to the Event Organizer Portal.
            </p>

            {modalError && (
              <div style={{ background: 'rgba(227,67,100,0.15)', color: '#E34364', padding: 10, borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
                ⚠️ {modalError}
              </div>
            )}

            <form onSubmit={handleCreateOrganizer}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 4 }}>Full Name *</label>
                <input
                  type="text"
                  required
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  placeholder="e.g. Jordan Smith"
                  style={inp(false)}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 4 }}>Email Address *</label>
                <input
                  type="email"
                  required
                  value={newOrgEmail}
                  onChange={(e) => setNewOrgEmail(e.target.value)}
                  placeholder="organizer@pixlybooth.com"
                  style={inp(false)}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 4 }}>Temporary Password *</label>
                <input
                  type="password"
                  required
                  value={newOrgPassword}
                  onChange={(e) => setNewOrgPassword(e.target.value)}
                  placeholder="••••••••"
                  style={inp(false)}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 4 }}>Phone Number</label>
                <input
                  type="tel"
                  value={newOrgPhone}
                  onChange={(e) => setNewOrgPhone(e.target.value)}
                  placeholder="e.g. (555) 234-5678"
                  style={inp(false)}
                />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setNewOrgModal(false)}
                  style={{ flex: 1, ...btn('rgba(255,255,255,0.08)', true), color: '#a0a0b8' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={orgCreating}
                  style={{ flex: 1, ...btn('#27C85A') }}
                >
                  {orgCreating ? 'Creating…' : '✓ Create Organizer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
